import React, { useState, useEffect, useCallback } from 'react';
import { doc, setDoc, getDoc, collection, getDocs, query, where, orderBy, deleteDoc, updateDoc, deleteField } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate } from 'react-router-dom';
import { isEncryptionEnabled, getStoredPassphrase, encryptData, decryptData, storePassphrase } from '../utils/encryption';
import Header from '../components/Header';
import ConfirmationDialog from '../components/ConfirmationDialog';
import PassphrasePrompt from '../components/PassphrasePrompt';
import '../styles.css';

function TireManager({ subscriptionStatus }) {
  const [tires, setTires] = useState([]);
  const [syncStatus, setSyncStatus] = useState('syncing');
  const [showAddTire, setShowAddTire] = useState(false);
  const [newTire, setNewTire] = useState({
    tireId: '',
    tireSize: '',
    tireCompound: '',
    treadDepth: '',
    laps: '',
    mileage: '',
    buildDate: ''
  });
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [tireToDiscard, setTireToDiscard] = useState(null);
  const [selectedTire, setSelectedTire] = useState(null);
  const [tireSessions, setTireSessions] = useState([]);
  const [showTireOverlay, setShowTireOverlay] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTireId, setEditingTireId] = useState(null);
  const [sortColumn, setSortColumn] = useState('tireId');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showHidden, setShowHidden] = useState(false);
  const [showPassphrasePrompt, setShowPassphrasePrompt] = useState(false);
  const [user, loading, error] = useAuthState(auth);
  const navigate = useNavigate();

  // Redirect to main app if user doesn't have Pro plan
  useEffect(() => {
    if (!loading && subscriptionStatus && subscriptionStatus.plan !== 'pro') {
      navigate('/app');
    }
  }, [subscriptionStatus, loading, navigate]);

  const loadData = useCallback(async () => {
    setSyncStatus('syncing');
    try {
      // Load tires
      const tiresQuery = query(
        collection(db, `users/${user.uid}/tires`),
        orderBy('dateAdded', 'asc')
      );
      const tiresSnap = await getDocs(tiresQuery);
      let tiresData = tiresSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Decrypt encrypted tires
      const encryptionEnabled = await isEncryptionEnabled(user, db);
      let hasEncryptedTires = false;
      if (encryptionEnabled) {
        let passphrase = getStoredPassphrase();
        hasEncryptedTires = tiresData.some(tire => tire.encrypted);

        if (hasEncryptedTires && !passphrase) {
          // Need to prompt for passphrase
          setShowPassphrasePrompt(true);
          return; // Don't load data yet
        }

        if (passphrase) {
          tiresData = tiresData.map(tire => {
            if (tire.encrypted) {
              try {
                const decryptedData = decryptData(tire.data, passphrase);
                // Handle both old and new formats
                if (decryptedData.tireId) {
                  // Old format: entire tire was encrypted
                  return {
                    id: tire.id,
                    ...decryptedData
                  };
                } else {
                  // New format: merge with plain metadata
                  return {
                    id: tire.id,
                    ...tire,
                    ...decryptedData
                  };
                }
              } catch (error) {
                console.error(`Failed to decrypt tire ${tire.id}:`, error);
                return tire; // Return encrypted tire as-is if decryption fails
              }
            }
            return tire;
          });
        }
      }

      // Load all sessions to calculate tire stats
      const sessionsQuery = query(collection(db, `users/${user.uid}/sessions`), orderBy('time', 'desc'));
      const sessionsSnap = await getDocs(sessionsQuery);
      let allSessions = sessionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Decrypt encrypted sessions
      if (encryptionEnabled) {
        let passphrase = getStoredPassphrase();
        const hasEncryptedSessions = allSessions.some(session => session.encrypted);

        if ((hasEncryptedTires || hasEncryptedSessions) && !passphrase) {
          // Need to prompt for passphrase
          setShowPassphrasePrompt(true);
          return; // Don't load data yet
        }

        if (passphrase) {
          allSessions = allSessions.map(session => {
            if (session.encrypted) {
              try {
                const decryptedData = decryptData(session.data, passphrase);
                // Handle both old and new formats
                if (decryptedData.vehicleId && decryptedData.eventId) {
                  // Old format: entire session was encrypted
                  return {
                    id: session.id,
                    ...decryptedData
                  };
                } else {
                  // New format: merge with plain metadata
                  return {
                    id: session.id,
                    ...session,
                    ...decryptedData
                  };
                }
              } catch (error) {
                console.error(`Failed to decrypt session ${session.id}:`, error);
                return session; // Return encrypted session as-is if decryption fails
              }
            }
            return session;
          });
        }
      }

      // Load circuits
      const circuitsQuery = query(collection(db, `users/${user.uid}/circuits`));
      const circuitsSnap = await getDocs(circuitsQuery);
      const circuitsMap = {};
      circuitsSnap.docs.forEach(doc => {
        circuitsMap[doc.id] = doc.data();
      });

      // Load events for circuit distances
      const eventsQuery = query(collection(db, `users/${user.uid}/events`));
      const eventsSnap = await getDocs(eventsQuery);
      const eventsMap = {};
      eventsSnap.docs.forEach(doc => {
        const eventData = doc.data();
        const circuit = Object.values(circuitsMap).find(c => c.name === eventData.circuit);
        eventsMap[doc.id] = {
          ...eventData,
          circuitDistance: parseFloat(circuit?.length || 5)
        };
      });

      // Calculate stats for each tire
      const tiresWithStats = tiresData.map(tire => {
        let totalLaps = parseInt(tire.laps || 0, 10);
        let totalMileage = parseInt(tire.mileage || 0, 10);

        allSessions.forEach(session => {
          const corners = ['Front Left', 'Front Right', 'Rear Left', 'Rear Right'];
          corners.forEach(corner => {
            const tireParam = `Tire ID (${corner})`;
            if (session.setup?.[tireParam] === tire.tireId) {
              const laps = parseInt(session.numLaps || 0, 10);
              const circuitDist = eventsMap[session.eventId]?.circuitDistance || 5;
              totalLaps += laps;
              totalMileage += laps * circuitDist;
            }
          });
        });

        return {
          ...tire,
          totalLaps,
          totalMileage
        };
      });

      setTires(tiresWithStats);

      setSyncStatus(navigator.onLine ? 'synced' : 'offline');
    } catch (err) {
      console.error('Load data error:', err);
      setSyncStatus(`Error: ${err.message}`);
    }
  }, [user]);

  useEffect(() => {
    if (loading) return;
    if (error || !user) {
      navigate('/');
      return;
    }
    loadData();
  }, [user, loading, error, navigate, loadData]);



  const resetNewTire = () => {
    setNewTire({
      tireId: '',
      tireSize: '',
      tireCompound: '',
      treadDepth: '',
      laps: '',
      mileage: '',
      buildDate: ''
    });
  };

  const handleTireSubmit = async () => {
    if (!newTire.tireId.trim()) {
      alert('Tire ID is required');
      return;
    }
    try {
      // Check if encryption is enabled
      const encryptionEnabled = await isEncryptionEnabled(user, db);
      let tireData;

      if (isEditing) {
        // Update existing tire
        const existingTire = tires.find(t => t.id === editingTireId);
        tireData = {
          ...newTire,
          tireId: newTire.tireId.trim(),
          dateAdded: existingTire.dateAdded, // Keep original date
          active: true
        };
      } else {
        // Add new tire
        tireData = {
          ...newTire,
          tireId: newTire.tireId.trim(),
          dateAdded: new Date().toISOString(),
          active: true
        };
      }

      // Encrypt if encryption is enabled
      if (encryptionEnabled) {
        const passphrase = getStoredPassphrase();
        if (!passphrase) {
          alert('Encryption is enabled but no passphrase found. Please check your settings.');
          return;
        }

        // Store metadata in plain text for queries, encrypt sensitive data
        const sensitiveData = {
          tireSize: tireData.tireSize,
          tireCompound: tireData.tireCompound,
          treadDepth: tireData.treadDepth,
          laps: tireData.laps,
          mileage: tireData.mileage,
          buildDate: tireData.buildDate
        };

        tireData = {
          // Plain text metadata for queries and display
          tireId: tireData.tireId,
          dateAdded: tireData.dateAdded,
          active: tireData.active,
          // Encrypted sensitive data
          encrypted: true,
          data: encryptData(sensitiveData, passphrase)
        };
      } else {
        // No encryption - ensure encrypted flag is false
        tireData.encrypted = false;
      }

      if (isEditing) {
        const tireRef = doc(db, `users/${user.uid}/tires/${editingTireId}`);
        if (encryptionEnabled) {
          // When encrypting, set encrypted fields and delete plain text fields
          await updateDoc(tireRef, {
            ...tireData,
            tireSize: deleteField(),
            tireCompound: deleteField(),
            treadDepth: deleteField(),
            laps: deleteField(),
            mileage: deleteField(),
            buildDate: deleteField()
          });
        } else {
          // When decrypting, set plain text fields and delete encrypted field
          await updateDoc(tireRef, {
            ...tireData,
            data: deleteField()
          });
        }
      } else {
        const docRef = doc(collection(db, `users/${user.uid}/tires`));
        await setDoc(docRef, tireData);
      }

      resetNewTire();
      setShowAddTire(false);
      setIsEditing(false);
      setEditingTireId(null);
      loadData();
    } catch (err) {
      console.error(isEditing ? 'Update tire error:' : 'Add tire error:', err);
      alert(`Failed to ${isEditing ? 'update' : 'add'} tire: ` + err.message);
    }
  };

  const editTire = (tire) => {
    setNewTire({ ...tire });
    setIsEditing(true);
    setEditingTireId(tire.id);
    setShowAddTire(true);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortValue = (tire, column) => {
    switch (column) {
      case 'tireId':
        return tire.tireId;
      case 'age':
        return parseInt(calculateAgeInMonths(tire.buildDate)) || 0;
      case 'compound':
        return tire.tireCompound || '';
      case 'treadDepth':
        return parseFloat(tire.treadDepth) || 0;
      case 'laps':
        return tire.totalLaps || 0;
      case 'mileage':
        return tire.totalMileage || 0;
      default:
        return '';
    }
  };

  const deleteTire = async () => {
    if (!tireToDiscard) return;
    try {
      await deleteDoc(doc(db, `users/${user.uid}/tires/${tireToDiscard.id}`));
      setTireToDiscard(null);
      setShowDiscardConfirm(false);
      loadData();
    } catch (err) {
      console.error('Delete tire error:', err);
      alert('Failed to delete tire: ' + err.message);
    }
  };

  const hideTire = async (tire) => {
    try {
      await setDoc(doc(db, `users/${user.uid}/tires/${tire.id}`), { ...tire, hidden: true }, { merge: true });
      setSelectedTire({ ...selectedTire, hidden: true });
      loadData();
    } catch (err) {
      console.error('Hide tire error:', err);
      alert('Failed to hide tire: ' + err.message);
    }
  };

  const unhideTire = async (tire) => {
    try {
      await setDoc(doc(db, `users/${user.uid}/tires/${tire.id}`), { ...tire, hidden: false }, { merge: true });
      setSelectedTire({ ...selectedTire, hidden: false });
      loadData();
    } catch (err) {
      console.error('Unhide tire error:', err);
      alert('Failed to unhide tire: ' + err.message);
    }
  };

  const loadTireSessions = async (tireId) => {
    try {
      // Load sessions that contain this tire ID
      const sessionsQuery = query(
        collection(db, `users/${user.uid}/sessions`),
        where('tireIds', 'array-contains', tireId),
        orderBy('time', 'desc')
      );
      const sessionsSnap = await getDocs(sessionsQuery);
      const allSessions = sessionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Load circuits
      const circuitsQuery = query(collection(db, `users/${user.uid}/circuits`));
      const circuitsSnap = await getDocs(circuitsQuery);
      const circuitsMap = {};
      circuitsSnap.docs.forEach(doc => {
        circuitsMap[doc.id] = doc.data();
      });

      // Build matching sessions with corners
      const matchingSessions = [];
      allSessions.forEach(session => {
        const corners = ['Front Left', 'Front Right', 'Rear Left', 'Rear Right'];
        corners.forEach(corner => {
          const tireParam = `Tire ID (${corner})`;
          if (session.setup?.[tireParam] === tireId) {
            matchingSessions.push({
              ...session,
              corner,
              laps: parseInt(session.numLaps || 0, 10)
            });
          }
        });
      });

      // Sort by time ascending
      matchingSessions.sort((a, b) => new Date(a.time) - new Date(b.time));

      // Load vehicle and event names
      const vehicleIds = [...new Set(matchingSessions.map(s => s.vehicleId))];
      const eventIds = [...new Set(matchingSessions.map(s => s.eventId))];

      // Batch fetch vehicles
      const vehiclePromises = vehicleIds.map(id => getDoc(doc(db, `users/${user.uid}/vehicles/${id}`)));
      const vehicleDocs = await Promise.all(vehiclePromises);
      const vehicles = {};
      vehicleDocs.forEach((docSnap, index) => {
        const id = vehicleIds[index];
        vehicles[id] = docSnap.exists() ? docSnap.data().name : 'Unknown Vehicle';
      });

      // Batch fetch events
      const eventPromises = eventIds.map(id => getDoc(doc(db, `users/${user.uid}/events/${id}`)));
      const eventDocs = await Promise.all(eventPromises);
      const events = {};
      eventDocs.forEach((docSnap, index) => {
        const id = eventIds[index];
        if (docSnap.exists()) {
          const eventData = docSnap.data();
          const circuit = Object.values(circuitsMap).find(c => c.name === eventData.circuit);
          const circuitDistance = parseFloat(circuit?.length || 5);
          events[id] = {
            name: eventData.name,
            circuitName: eventData.circuit,
            circuitDistance
          };
        } else {
          events[id] = { name: 'Unknown Event', circuitName: 'Unknown', circuitDistance: 5 };
        }
      });

      const sessionsWithNames = matchingSessions.map(session => ({
        ...session,
        vehicleName: vehicles[session.vehicleId] || 'Unknown',
        eventName: events[session.eventId]?.name || 'Unknown',
        circuitName: events[session.eventId]?.circuitName || 'Unknown',
        sessionName: session.sessionName || 'Unnamed',
        mileage: session.laps * (events[session.eventId]?.circuitDistance || 5)
      }));

      setTireSessions(sessionsWithNames);
    } catch (err) {
      console.error('Load tire sessions error:', err);
    }
  };

  const selectTire = (tire) => {
    setSelectedTire(tire);
    loadTireSessions(tire.tireId);
    setShowTireOverlay(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const calculateAgeInMonths = (buildDateString) => {
    if (!buildDateString) return '';
    const now = new Date();
    const build = new Date(buildDateString);
    const months = (now.getFullYear() - build.getFullYear()) * 12 + (now.getMonth() - build.getMonth());
    return `${Math.max(0, months)} months`;
  };

  const filteredTires = showHidden ? tires : tires.filter(tire => !tire.hidden);
  const sortedTires = [...filteredTires].sort((a, b) => {
    const aVal = getSortValue(a, sortColumn);
    const bVal = getSortValue(b, sortColumn);
    if (typeof aVal === 'string') {
      return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    } else {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
  });

  return (
    <div>
      <Header syncStatus={syncStatus} />
      <div className="tire-manager-container">
        <div className="page-title">
        <h1>Tire Manager</h1>
        <button onClick={() => navigate('/')} className="close-button">✕</button>
      </div>

      <div className="table-container">
        <table className="full-size-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('tireId')} style={{ cursor: 'pointer' }}>
                Tire ID {sortColumn === 'tireId' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th>Tire Size</th>
              <th onClick={() => handleSort('compound')} style={{ cursor: 'pointer' }}>
                Compound {sortColumn === 'compound' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('treadDepth')} style={{ cursor: 'pointer' }}>
                Tread Depth {sortColumn === 'treadDepth' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('laps')} style={{ cursor: 'pointer' }}>
                Laps {sortColumn === 'laps' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('mileage')} style={{ cursor: 'pointer' }}>
                Mileage {sortColumn === 'mileage' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('age')} style={{ cursor: 'pointer' }}>
                Age {sortColumn === 'age' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody>
            {syncStatus === 'syncing' ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px'
                  }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      border: '3px solid rgba(59, 130, 246, 0.3)',
                      borderTop: '3px solid #3B82F6',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Loading tire data...
                  </div>
                </td>
              </tr>
            ) : (
              sortedTires.map(tire => (
                <tr key={tire.id} onClick={() => selectTire(tire)} style={{ cursor: 'pointer', color: tire.hidden ? '#888' : 'inherit' }}>
                  <td>
                    <div>{tire.tireId}</div>
                    <div>Date Added: {formatDate(tire.dateAdded)}</div>
                  </td>
                  <td>{tire.tireSize || ''}</td>
                  <td>{tire.tireCompound || ''}</td>
                    <td>{tire.treadDepth ? `${tire.treadDepth} mm` : ''}</td>
                    <td>{tire.totalLaps || 0}</td>
                    <td>{tire.totalMileage ? `${tire.totalMileage.toFixed(2)} km` : '0.00 km'}</td>
                  <td>{calculateAgeInMonths(tire.buildDate)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {showAddTire && (
        <div className="overlay-card" style={{ zIndex: 1002 }}>
          <div className="card-container">
            <button
              onClick={() => { 
                setShowAddTire(false); 
                resetNewTire();
                setIsEditing(false);
                setEditingTireId(null);
              }}
              className="close-button"
              style={{ position: 'absolute', top: '10px', right: '10px' }}
            >
              ✕
            </button>
            <h2 style={{ marginBottom: '15px' }}>{isEditing ? 'Edit Tire' : 'Add New Tire'}</h2>
            <input
              type="text"
              placeholder="Tire ID (e.g., S01 FL)"
              value={newTire.tireId}
              onChange={e => setNewTire({ ...newTire, tireId: e.target.value })}
              className="modal-input"
            />
            <input
              type="text"
              placeholder="Tire Size (e.g., 325/30/20)"
              value={newTire.tireSize}
              onChange={e => setNewTire({ ...newTire, tireSize: e.target.value })}
              className="modal-input"
            />
            <input
              type="text"
              placeholder="Tire Compound (e.g., Mich PS4S)"
              value={newTire.tireCompound}
              onChange={e => setNewTire({ ...newTire, tireCompound: e.target.value })}
              className="modal-input"
            />
            <input
              type="number"
              placeholder="Tread Depth (mm)"
              value={newTire.treadDepth}
              onChange={e => setNewTire({ ...newTire, treadDepth: e.target.value })}
              className="modal-input"
            />
            <input
              type="number"
              placeholder="Initial Laps"
              value={newTire.laps}
              onChange={e => setNewTire({ ...newTire, laps: e.target.value })}
              className="modal-input"
            />
            <input
              type="number"
              placeholder="Initial Mileage (km)"
              value={newTire.mileage}
              onChange={e => setNewTire({ ...newTire, mileage: e.target.value })}
              className="modal-input"
            />
            <input
              type="date"
              placeholder="Build Date"
              value={newTire.buildDate}
              onChange={e => setNewTire({ ...newTire, buildDate: e.target.value })}
              className="modal-input"
            />
            <div className="modal-actions">
              <button onClick={handleTireSubmit} className="btn btn-save">{isEditing ? 'Save' : 'Add'}</button>
              <button onClick={() => { 
                setShowAddTire(false); 
                resetNewTire();
                setIsEditing(false);
                setEditingTireId(null);
              }} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}
      {showDiscardConfirm && (
        <ConfirmationDialog
          message={`Are you sure you want to delete tire "${tireToDiscard?.tireId}"? This action cannot be undone.`}
          onConfirm={deleteTire}
          onCancel={() => { setShowDiscardConfirm(false); setTireToDiscard(null); }}
        />
      )}
      {showTireOverlay && selectedTire && (
        <div className="overlay-card tire-manager-overlay">
          <div className="card-container">
            <button
              onClick={() => setShowTireOverlay(false)}
              className="close-button"
              style={{ position: 'absolute', top: '10px', right: '10px' }}
            >
              ✕
            </button>
            <h2>{selectedTire.tireId}</h2>
            <table className="full-size-table">
              <thead>
                <tr>
                  <th>Session Name</th>
                  <th>Circuit</th>
                  <th>Laps</th>
                  <th>Mileage</th>
                  <th>Vehicle</th>
                  <th>Corner</th>
                </tr>
              </thead>
              <tbody>
                {tireSessions.map(session => (
                  <tr key={session.id}>
                    <td>{session.eventName} - {session.sessionName}</td>
                    <td>{session.circuitName}</td>
                    <td>{session.laps}</td>
                    <td>{session.mileage.toFixed(2)} km</td>
                    <td>{session.vehicleName}</td>
                    <td>{session.corner}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => editTire(selectedTire)}
                  className="btn btn-secondary"
                >
                  Edit Tire
                </button>
                <button
                  onClick={() => selectedTire.hidden ? unhideTire(selectedTire) : hideTire(selectedTire)}
                  className={selectedTire.hidden ? "btn btn-primary" : "btn btn-secondary"}
                >
                  {selectedTire.hidden ? "Unhide Tire" : "Hide Tire"}
                </button>
              </div>
              <button
                onClick={() => { setTireToDiscard(selectedTire); setShowDiscardConfirm(true); }}
                className="btn btn-danger"
              >
                Delete Tire
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
      <div className="session-buttons">
        <button onClick={() => { setShowAddTire(true); setIsEditing(false); setEditingTireId(null); }} className="btn btn-save">Add New Tire</button>
        <button onClick={() => setShowHidden(!showHidden)} className="btn btn-secondary">{showHidden ? 'Hide Hidden Tires' : 'Show Hidden Tires'}</button>
      </div>

      {showPassphrasePrompt && (
        <PassphrasePrompt
          message="Enter your encryption passphrase to access tire data:"
          onSuccess={(passphrase) => {
            setShowPassphrasePrompt(false);
            // Store the passphrase for future use
            storePassphrase(passphrase);
            loadData(); // Retry loading data with passphrase
          }}
          onCancel={() => {
            setShowPassphrasePrompt(false);
            navigate('/settings'); // Redirect to settings
          }}
        />
      )}
    </div>
  );
}

export default TireManager;